import '@/index.css';
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';

import { AuthProvider, useAuth } from '@/lib/auth';
import { AppShell } from '@/components/AppShell';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { AriWidget } from '@/components/AriWidget';
import { RemoddashPromo } from '@/components/RemoddashPromo';
import { AdminShell } from '@/components/AdminShell';
import { FEATURES } from '@/config/features';

import Landing from '@/pages/Landing';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import VerifyEmail from '@/pages/VerifyEmail';
import Home from '@/pages/Home';
import Seek from '@/pages/Seek';
import Results from '@/pages/Results';
import Vault from '@/pages/Vault';
import Alerts from '@/pages/Alerts';
import Rules from '@/pages/Rules';
import Collections from '@/pages/Collections';
import Connect from '@/pages/Connect';
import Settings from '@/pages/Settings';
import Billing from '@/pages/Billing';
import Subscribe from '@/pages/Subscribe';
import Team from '@/pages/Team';
import AcceptInvite from '@/pages/AcceptInvite';
import { PWAInstallPrompt } from '@/components/PWAInstallPrompt';
import Terms from '@/pages/Terms';
import Privacy from '@/pages/Privacy';
import About from '@/pages/About';
import Support from '@/pages/Support';
import AdminLogin from '@/pages/AdminLogin';
import AdminLive from '@/pages/admin/AdminLive';
import AdminUsers from '@/pages/admin/AdminUsers';
import AdminTickets from '@/pages/admin/AdminTickets';
import AdminThirdPartyApps from '@/pages/admin/AdminThirdPartyApps';
import {
  AdminSubscriptions,
  AdminAnalytics,
  AdminMarketplaces,
  AdminErrors,
  AdminSystem,
} from '@/pages/admin/AdminPanels';

const Shelled = ({ children }) => <AppShell>{children}</AppShell>;

// Aug 2026 §14 — Seek is Home. Chat-first Ari surface (was Home.jsx) is now
// the default landing. Legacy `/` and `/home` redirect to `/seek`.
const RootRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Landing />;
  if (!user.has_active_access) return <Navigate to="/subscribe" replace />;
  return <Navigate to="/seek" replace />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" theme="light" richColors closeButton />
        <PWAInstallPrompt />
        <Routes>
          <Route path="/" element={<RootRoute />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot" element={<ForgotPassword />} />
          <Route path="/reset" element={<ResetPassword />} />
          <Route path="/verify" element={<VerifyEmail />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/about" element={<About />} />

          <Route path="/billing" element={
            <ProtectedRoute>
              <Shelled><Billing /></Shelled>
            </ProtectedRoute>
          } />
          <Route path="/subscribe" element={
            <ProtectedRoute>
              <Subscribe />
            </ProtectedRoute>
          } />
          <Route path="/billing/success" element={
            <ProtectedRoute>
              <Shelled><Billing /></Shelled>
            </ProtectedRoute>
          } />
          <Route path="/billing/cancel" element={
            <ProtectedRoute>
              <Shelled><Billing /></Shelled>
            </ProtectedRoute>
          } />
          <Route path="/billing/addon-success" element={
            <ProtectedRoute>
              <Shelled><Billing /></Shelled>
            </ProtectedRoute>
          } />

          <Route path="/home" element={<Navigate to="/seek" replace />} />
          <Route path="/seek" element={
            <ProtectedRoute requireAccess>
              <Shelled><Home /></Shelled>
            </ProtectedRoute>
          } />
          <Route path="/seek/advanced" element={
            <ProtectedRoute requireAccess>
              <Shelled><Seek /></Shelled>
            </ProtectedRoute>
          } />
          <Route path="/results/:scanId" element={
            <ProtectedRoute requireAccess>
              <Shelled><Results /></Shelled>
            </ProtectedRoute>
          } />
          <Route path="/vault" element={
            <ProtectedRoute requireAccess>
              <Shelled><Vault /></Shelled>
            </ProtectedRoute>
          } />
          <Route path="/vault/:tab" element={
            <ProtectedRoute requireAccess>
              <Shelled><Vault /></Shelled>
            </ProtectedRoute>
          } />
          <Route path="/alerts" element={
            <ProtectedRoute requireAccess>
              <Shelled><Alerts /></Shelled>
            </ProtectedRoute>
          } />
          <Route path="/collections" element={
            <ProtectedRoute requireAccess>
              <Shelled><Collections /></Shelled>
            </ProtectedRoute>
          } />
          <Route path="/connect" element={
            <ProtectedRoute requireAccess>
              <Shelled><Connect /></Shelled>
            </ProtectedRoute>
          } />
          <Route path="/history" element={<Navigate to="/vault/history" replace />} />
          <Route path="/results-feed" element={<Navigate to="/vault/results" replace />} />
          <Route path="/rules" element={
            <ProtectedRoute requireAccess>
              <Shelled><Rules /></Shelled>
            </ProtectedRoute>
          } />
          <Route path="/team" element={
            <ProtectedRoute requireAccess>
              <Shelled><Team /></Shelled>
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute>
              <Shelled><Settings /></Shelled>
            </ProtectedRoute>
          } />
          <Route path="/support" element={
            <ProtectedRoute>
              <Shelled><Support /></Shelled>
            </ProtectedRoute>
          } />

          {/* Super Admin — Owner model v2.1 */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminShell />}>
            <Route index element={<AdminLive />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="subscriptions" element={<AdminSubscriptions />} />
            <Route path="tickets" element={<AdminTickets />} />
            <Route path="tickets/:ticketId" element={<AdminTickets />} />
            <Route path="third-party-apps" element={<AdminThirdPartyApps />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="marketplaces" element={<AdminMarketplaces />} />
            <Route path="errors" element={<AdminErrors />} />
            <Route path="system" element={<AdminSystem />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <AriWidget />
        {FEATURES.remoddashPromo && <RemoddashPromo />}
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
